<template>
    <div class="min-h-screen bg-slate-50/70">
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6 space-y-6">
            <div class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div class="px-6 py-6 bg-gradient-to-br from-white via-slate-50 to-amber-50">
                    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                        <div class="flex items-start gap-4">
                            <span class="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 shadow-sm">
                                <v-icon name="hi-solid-bell" class="w-6 h-6" />
                            </span>
                            <div>
                                <h1 class="text-2xl font-bold tracking-tight text-slate-900">
                                    Inbox Promemoria
                                </h1>
                                <p class="text-sm text-slate-500 mt-1 max-w-2xl">
                                    Promemoria non assegnati in attesa di smistamento ai reparti.
                                </p>
                            </div>
                        </div>

                        <button
                            @click="showCreateModal = true"
                            class="inline-flex items-center justify-center gap-2 bg-amber-500 text-white px-4 py-2.5 rounded-xl hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 text-sm font-semibold shadow-sm transition-colors"
                        >
                            <v-icon name="hi-plus" class="w-4 h-4" />
                            Nuovo promemoria
                        </button>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">In inbox</p>
                                    <p class="text-xl font-bold text-slate-900 mt-0.5">{{ reminders.length }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-inbox" class="w-5 h-5" />
                                </span>
                            </div>
                        </div>

                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">Reparti attivi</p>
                                    <p class="text-xl font-bold text-indigo-700 mt-0.5">{{ departments.length }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-user-group" class="w-5 h-5" />
                                </span>
                            </div>
                        </div>

                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">Da smistare</p>
                                    <p class="text-xl font-bold text-rose-600 mt-0.5">{{ unassignedCount }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-exclamation" class="w-5 h-5" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="p-6">
                    <div v-if="localReminders.length === 0" class="flex flex-col items-center justify-center py-16 text-slate-400">
                        <v-icon name="hi-solid-inbox" class="w-12 h-12 mb-3 opacity-40" />
                        <p class="text-sm font-medium">Nessun promemoria in inbox</p>
                        <p class="text-xs mt-1">I nuovi promemoria non assegnati appariranno qui.</p>
                    </div>

                    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <div
                            v-for="reminder in localReminders"
                            :key="reminder.id"
                            class="group relative bg-amber-50/60 border border-amber-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div class="absolute top-3 right-3">
                                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                                    {{ statusLabel(reminder.status) }}
                                </span>
                            </div>

                            <h3 class="font-semibold text-slate-900 pr-24 leading-snug">{{ reminder.title }}</h3>

                            <p v-if="reminder.description" class="text-xs text-slate-500 mt-1 line-clamp-2">
                                {{ reminder.description }}
                            </p>

                            <div class="flex items-center gap-1.5 mt-3 text-xs text-slate-400">
                                <v-icon name="hi-solid-user" class="w-3.5 h-3.5" />
                                <span>{{ reminder.creator?.name ?? '—' }}</span>
                                <span class="ml-auto">{{ formatDate(reminder.created_at) }}</span>
                            </div>

                            <div class="mt-3 pt-3 border-t border-amber-200 flex items-center gap-2">
                                <select
                                    v-model="assignTargets[reminder.id]"
                                    class="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="">Seleziona reparto…</option>
                                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                        {{ dept.name }}
                                    </option>
                                </select>

                                <button
                                    @click="assignDepartment(reminder)"
                                    :disabled="!assignTargets[reminder.id] || submitting === reminder.id"
                                    class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <v-icon name="hi-solid-arrow-right" class="w-3.5 h-3.5" />
                                    Assegna
                                </button>

                                <button
                                    @click="take(reminder)"
                                    :disabled="submitting === reminder.id"
                                    class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <v-icon name="hi-solid-check" class="w-3.5 h-3.5" />
                                    Prendi in carico
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <CreateReminderModal
            :visible="showCreateModal"
            :departments="departments"
            @close="showCreateModal = false"
            @created="onCreated"
        />
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import CreateReminderModal from './Components/CreateReminderModal.vue';

export default {
    name: 'PromemoriaInbox',

    components: { BreadCrumbs, CreateReminderModal },

    props: {
        reminders: {
            type: Array,
            default: () => [],
        },
        departments: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            showCreateModal: false,
            localReminders: [...this.reminders],
            assignTargets: {},
            submitting: null,
            navigation: [
                { name: 'Home', href: '/' },
                { name: 'Attività', href: '#' },
                { name: 'Inbox Promemoria', href: '#' },
            ],
        };
    },

    computed: {
        unassignedCount() {
            return this.localReminders.filter(r => !r.department_id).length;
        },
    },

    methods: {
        formatDate(value) {
            if (!value) return '—';
            return new Date(value).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },

        statusLabel(status) {
            const map = {
                nuovo: 'Nuovo',
                assegnato_reparto: 'Assegnato',
                preso_in_carico: 'In carico',
                annullato: 'Annullato',
            };
            return map[status] ?? status;
        },

        assignDepartment(reminder) {
            const departmentId = this.assignTargets[reminder.id];
            if (!departmentId || this.submitting) return;

            this.submitting = reminder.id;

            axios.patch(route('modules.attivita.promemoria.assegna-reparto', { reminder: reminder.id }), {
                department_id: departmentId,
            })
                .then(() => {
                    this.localReminders = this.localReminders.filter(r => r.id !== reminder.id);
                })
                .catch(() => {
                    alert('Errore durante l\'assegnazione. Riprova.');
                })
                .finally(() => {
                    this.submitting = null;
                });
        },

        take(reminder) {
            if (this.submitting) return;

            this.submitting = reminder.id;

            axios.post(route('modules.attivita.promemoria.prendi-in-carico', { reminder: reminder.id }))
                .then(() => {
                    this.localReminders = this.localReminders.filter(r => r.id !== reminder.id);
                })
                .catch(() => {
                    alert('Errore durante la presa in carico. Riprova.');
                })
                .finally(() => {
                    this.submitting = null;
                });
        },

        onCreated(newReminder) {
            this.showCreateModal = false;
            if (newReminder && !newReminder.department_id) {
                this.localReminders.unshift(newReminder);
            }
        },
    },
};
</script>
